import React from "react";
import { X } from "lucide-react";
import ProductUpdatePublicationView from "./ProductUpdatePublicationView.jsx";
import { TRANSIA_BANNER } from "./ProductUpdateCoverPicker.jsx";
import { attachLinkedImage, automaticCover, productUpdateAnnouncementPages } from "../utils/productUpdateAnnouncementPages.js";

// Shared presentation for the live announcement and the local animation test.
export default function ProductUpdateAnnouncementDialog({ content, dialogRef, titleRef, page: controlledPage,
  onPageChange, onDismiss, onViewJournal, saving = false, error = "", testMode = false }) {
  const ownDialogRef = React.useRef(null);
  const ownTitleRef = React.useRef(null);
  const dialog = dialogRef || ownDialogRef;
  const title = titleRef || ownTitleRef;
  const titleId = React.useId();
  const [testPage, setTestPage] = React.useState(0);
  const page = controlledPage ?? testPage;
  const setPage = onPageChange || setTestPage;
  const cover = content?.cover ?? automaticCover(content?.image, (content?.sections || []).flatMap(section => section.items).map(item => item.item_id));
  // A common image chosen as banner stays whole there instead of joining its items.
  const resolved = React.useMemo(() => cover === "image" ? content : attachLinkedImage(content), [content, cover]);
  const coverImage = cover === "banner" ? null : cover !== "image"
    ? resolved?.sections?.flatMap(section => section.items).find(item => item.item_id === cover)?.image : resolved?.image;
  const pages = React.useMemo(() => productUpdateAnnouncementPages(resolved), [resolved]);
  const currentPage = Math.max(0, Math.min(page, pages.length - 1));
  const current = pages[currentPage];
  const totalItems = pages.at(-1)?.end || 0;
  // Attention effects run until the reader first interacts with the announcement.
  const [attention, setAttention] = React.useState(true);
  const acknowledge = () => setAttention(false);
  React.useEffect(() => setAttention(true), [content]);
  // While the announcement waits in a background tab, the tab title blinks.
  React.useEffect(() => {
    if (!attention) return undefined;
    const original = document.title;
    let timer = null;
    const sync = () => {
      clearInterval(timer); timer = null; document.title = original;
      if (document.visibilityState === "hidden") {
        let tick = 0;
        timer = setInterval(() => {
          document.title = dialog.current?.open && ++tick % 2 ? "● Du nouveau dans TransIA" : original;
        }, 1300);
      }
    };
    sync();
    document.addEventListener("visibilitychange", sync);
    return () => { document.removeEventListener("visibilitychange", sync); clearInterval(timer); document.title = original; };
  }, [attention, dialog]);

  React.useEffect(() => {
    if (!testMode) return undefined;
    const element = dialog.current;
    const previous = document.activeElement;
    element.showModal();
    title.current?.focus();
    return () => {
      if (element.open) element.close();
      if (previous?.isConnected) previous.focus();
    };
  }, [testMode, dialog, title]);

  function trapFocus(event) {
    if (event.key !== "Tab") return;
    const controls = [...dialog.current.querySelectorAll('button:not([disabled]), a[href], [tabindex="0"]')]
      .filter(element => element.getClientRects().length > 0);
    if (!controls.length) { event.preventDefault(); title.current?.focus(); return; }
    const first = controls[0], last = controls.at(-1);
    if (event.shiftKey && (document.activeElement === first || document.activeElement === title.current)) {
      event.preventDefault(); last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault(); first.focus();
    }
  }

  return <dialog ref={dialog} className={`product-update-announcement${attention ? " is-attention" : ""}`} aria-labelledby={titleId}
    data-product-update-test={testMode ? "true" : undefined} onKeyDown={event => { acknowledge(); trapFocus(event); }}
    onPointerEnter={acknowledge} onPointerDown={acknowledge} onFocus={event => { if (event.target !== title.current) acknowledge(); }}
    onCancel={event => { event.preventDefault(); void onDismiss("escape"); }}>
    <header className="product-update-announcement-header">
      <img src={coverImage?.data_url || TRANSIA_BANNER} alt="" />
      <div><p>{testMode ? "Test local — aucune diffusion" : "Le Journal TransIA"} · {totalItems} {totalItems === 1 ? "nouveauté" : "nouveautés"}</p>
        <h2 id={titleId} ref={title} tabIndex={-1}>Du nouveau dans TransIA</h2></div>
      <button type="button" className="pu-button" aria-label={testMode ? "Fermer l'annonce de test" : "Fermer l'annonce"}
        disabled={saving} onClick={() => void onDismiss("cross")}><X size={18} /></button>
    </header>
    <div className="product-update-announcement-body">
      {/* The common image not used as cover shows below; items keep their own images. */}
      <ProductUpdatePublicationView key={currentPage} content={{ sections: current ? [current] : [],
        ...(resolved?.image && coverImage !== resolved.image ? { image: resolved.image }
          : resolved?.image?.additional_images?.length ? { image: resolved.image.additional_images[0] } : {}) }} animated />
      {error && <p className="pu-error" role="alert">{error}</p>}
    </div>
    {pages.length > 1 && <nav className="product-update-pages" aria-label="Navigation des nouveautés">
      <button className="pu-button" disabled={currentPage === 0} onClick={() => setPage(currentPage - 1)}>Précédent</button>
      <div className="product-update-page-counter" aria-live="polite">
        <span>Nouveautés {current.start}–{current.end} sur {totalItems}</span><small>Page {currentPage + 1} sur {pages.length}</small>
      </div>
      <button className="pu-button" disabled={currentPage === pages.length - 1} onClick={() => setPage(currentPage + 1)}>Suivant</button>
    </nav>}
    <footer className="product-update-announcement-footer">
      {!testMode && <button className="pu-button" disabled={saving} onClick={() => void onViewJournal()}>Voir le Journal</button>}
      <button className="pu-button pu-button--primary" disabled={saving} onClick={() => void onDismiss("understood")}>
        {testMode ? "Fermer le test" : saving ? "Enregistrement…" : "Compris"}
      </button>
    </footer>
  </dialog>;
}
